import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import {
    fetchCalendarEventsForOffice,
    fetchMicrosoftRoomMappings,
    fetchRoomsForOffice,
    fetchTagsForOffice,
    resolveRoomImageLinks,
    type RoomTag,
    type SessionClaims,
} from '../api'
import { filterRoomGroups, mapSpacesToGroups, type CalendarEvent, type Room, type RoomGroup } from '../models/rooms'

const DEFAULT_LOADING_MESSAGE = 'Loading rooms...'
const ROOM_LOAD_FAILED_MESSAGE = 'Unable to load rooms'

function toggleGroupCollapsedState(groups: RoomGroup[], groupId: string) {
    return groups.map((group) => (group.id === groupId ? { ...group, collapsed: !group.collapsed } : group))
}

type UseRoomDataArgs = {
    enabled: boolean
    sessionToken: string
    sessionClaims: SessionClaims | null
    selectedOfficeId: string
    microsoftTenantId: string
    currentEventDate: string
    appointmentStart: Date | null
    appointmentEnd: Date | null
    searchTerm: string
    activeTagIds: Set<string>
    minCapacity: number | null
    onTagsLoaded: (tags: RoomTag[]) => void
}

export function useRoomData({
    enabled,
    sessionToken,
    sessionClaims,
    selectedOfficeId,
    microsoftTenantId,
    currentEventDate,
    appointmentStart,
    appointmentEnd,
    searchTerm,
    activeTagIds,
    minCapacity,
    onTagsLoaded,
}: UseRoomDataArgs) {
    const requestIdRef = useRef(0)

    const [availableTags, setAvailableTags] = useState<RoomTag[]>([])
    const [roomGroups, setRoomGroups] = useState<RoomGroup[]>([])
    const [roomEvents, setRoomEvents] = useState<CalendarEvent[]>([])
    const [allMappedRooms, setAllMappedRooms] = useState<Room[]>([])
    const [roomsLoadingMessage, setRoomsLoadingMessage] = useState(DEFAULT_LOADING_MESSAGE)
    const [roomsLoaded, setRoomsLoaded] = useState(false)

    const refreshOfficeData = useCallback(async () => {
        if (!enabled || !sessionToken || !sessionClaims || !selectedOfficeId || !microsoftTenantId) {
            return
        }

        const requestId = ++requestIdRef.current
        setRoomsLoadingMessage(DEFAULT_LOADING_MESSAGE)
        setRoomsLoaded(false)

        try {
            const [roomsResponse, tagsResponse, eventsResponse, roomMappingsResponse] = await Promise.all([
                fetchRoomsForOffice(sessionToken, sessionClaims.companyId, selectedOfficeId),
                fetchTagsForOffice(sessionToken, sessionClaims.companyId, selectedOfficeId),
                currentEventDate
                    ? fetchCalendarEventsForOffice(sessionToken, selectedOfficeId, currentEventDate)
                    : Promise.resolve({ items: [] }),
                fetchMicrosoftRoomMappings(sessionToken, microsoftTenantId).catch((error) => {
                    console.error('Failed to load Microsoft room mappings.', error)
                    return []
                }),
            ])

            const roomsWithSignedImages = await resolveRoomImageLinks(sessionToken, roomsResponse)

            if (requestIdRef.current !== requestId) {
                return
            }

            setAvailableTags(tagsResponse)
            onTagsLoaded(tagsResponse)
            setRoomEvents(eventsResponse.items)

            const roomMappings = new Map(roomMappingsResponse.map((mapping) => [mapping.reservableRoomId, mapping]))
            const mappedGroups = mapSpacesToGroups(roomsWithSignedImages.spaces, roomMappings)

            setRoomGroups(mappedGroups)
            setAllMappedRooms(mappedGroups.flatMap((group) => group.rooms))
            setRoomsLoaded(true)
        } catch (error) {
            console.error(error)
            if (requestIdRef.current === requestId) {
                setRoomGroups([])
                setAllMappedRooms([])
                setRoomsLoadingMessage(ROOM_LOAD_FAILED_MESSAGE)
                setRoomsLoaded(true)
            }
        }
    }, [currentEventDate, enabled, microsoftTenantId, onTagsLoaded, selectedOfficeId, sessionClaims, sessionToken])

    useEffect(() => {
        void refreshOfficeData()
    }, [refreshOfficeData])

    const filteredGroups = useMemo(
        () =>
            filterRoomGroups({
                roomGroups,
                roomEvents,
                appointmentStart,
                appointmentEnd,
                activeTagIds,
                minCapacity,
                searchTerm,
            }),
        [activeTagIds, appointmentEnd, appointmentStart, minCapacity, roomEvents, roomGroups, searchTerm],
    )

    const toggleGroup = useCallback((groupId: string) => {
        setRoomGroups((previous) => toggleGroupCollapsedState(previous, groupId))
    }, [])

    return {
        availableTags,
        allMappedRooms,
        filteredGroups,
        roomsLoadingMessage,
        roomsLoaded,
        toggleGroup,
    }
}
